import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CanvasWorkspace } from 'types/canvas';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useState } from 'react';
import { SGTooltip } from 'components/core';

interface CanvasWorkspaceTabsProps {
  workspaces: CanvasWorkspace[];
  activeWorkspaceId: number;
  setActiveWorkspaceId: (id: number) => void;
  handleRemoveWorkspace: (id: number) => void;
  handleAddWorkspace: (name: string) => void;
  handleEditWorkspaceName: (id: number, newName: string) => void;
}

const CanvasWorkspaceTabs = ({
  workspaces,
  activeWorkspaceId,
  setActiveWorkspaceId,
  handleRemoveWorkspace,
  handleAddWorkspace,
  handleEditWorkspaceName,
}: CanvasWorkspaceTabsProps) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [newWorkspaceDialogOpen, setNewWorkspaceDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<number | null>(
    null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [workspaceToEdit, setWorkspaceToEdit] = useState<number | null>(null);
  const [editWorkspaceName, setEditWorkspaceName] = useState('');

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
  const menuOpen = anchorEl != null;

  const closeMenu = () => setAnchorEl(null);

  const handleSelectWorkspace = (id: number) => {
    setActiveWorkspaceId(id);
    closeMenu();
  };

  const handleDeleteClick = (workspaceId: number) => {
    setWorkspaceToDelete(workspaceId);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (workspaceToDelete != null) {
      handleRemoveWorkspace(workspaceToDelete);
    }
    setConfirmDeleteDialogOpen(false);
    setWorkspaceToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteDialogOpen(false);
    setWorkspaceToDelete(null);
  };

  const handleConfirmAddWorkspace = () => {
    handleAddWorkspace(newWorkspaceName);
    setNewWorkspaceDialogOpen(false);
    setNewWorkspaceName('');
  };

  const handleEditClick = (workspaceId: number) => {
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (workspace != null) {
      setWorkspaceToEdit(workspaceId);
      setEditWorkspaceName(workspace.name);
      setEditDialogOpen(true);
    }
  };

  const handleConfirmEdit = () => {
    if (workspaceToEdit != null && editWorkspaceName.trim()) {
      handleEditWorkspaceName(workspaceToEdit, editWorkspaceName.trim());
    }
    setEditDialogOpen(false);
    setWorkspaceToEdit(null);
    setEditWorkspaceName('');
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setWorkspaceToEdit(null);
    setEditWorkspaceName('');
  };

  const workspaceToDeleteName = workspaces.find(
    (w) => w.id === workspaceToDelete,
  )?.name;

  return (
    <>
      <SGTooltip title="Switch workspace" placement="top">
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={
            <ArrowDropDownIcon
              sx={{
                transition: 'transform 120ms',
                transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          }
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: 13, md: 14 },
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            minWidth: 0,
            px: 4,
            gap: 0.75,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeWorkspace?.name ?? 'Workspace'}
          </Box>
        </Button>
      </SGTooltip>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 240,
              maxWidth: 320,
              mt: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              p: 0.5,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                py: 0.875,
                px: 1.25,
                fontSize: 13,
              },
            },
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 0.5, pb: 0.75 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
              lineHeight: 1.2,
            }}
          >
            Workspaces
          </Typography>
        </Box>

        {workspaces.map((workspace) => {
          const isActive = workspace.id === activeWorkspaceId;
          return (
            <MenuItem
              key={workspace.id}
              selected={isActive}
              onClick={() => handleSelectWorkspace(workspace.id)}
              sx={{
                '& .workspace-actions': {
                  opacity: { xs: 1, md: 0 },
                  transition: 'opacity 120ms',
                },
                '&:hover .workspace-actions': { opacity: 1 },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 22 }}>
                {isActive ? (
                  <CheckIcon
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                ) : (
                  <Box sx={{ width: 16, height: 16 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={workspace.name}
                primaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  },
                }}
              />
              <Box
                className="workspace-actions"
                sx={{ display: 'flex', gap: 0.25, ml: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <SGTooltip title="Rename" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => {
                      closeMenu();
                      handleEditClick(workspace.id);
                    }}
                    sx={{
                      p: 0.25,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </SGTooltip>
                {workspaces.length > 1 && (
                  <SGTooltip title="Delete" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => {
                        closeMenu();
                        handleDeleteClick(workspace.id);
                      }}
                      sx={{
                        p: 0.25,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </SGTooltip>
                )}
              </Box>
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.5, mx: 0.5 }} />

        <MenuItem
          onClick={() => {
            closeMenu();
            setNewWorkspaceName('');
            setNewWorkspaceDialogOpen(true);
          }}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 22, color: 'inherit' }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="New workspace"
            primaryTypographyProps={{
              sx: { fontSize: 13, fontWeight: 600 },
            }}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={newWorkspaceDialogOpen}
        onClose={() => setNewWorkspaceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Workspace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workspace Name"
            fullWidth
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmAddWorkspace();
              }
            }}
            variant="outlined"
            sx={{
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewWorkspaceDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddWorkspace}
            variant="contained"
            disabled={!newWorkspaceName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Workspace Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workspace Name"
            fullWidth
            value={editWorkspaceName}
            onChange={(e) => setEditWorkspaceName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmEdit();
              }
            }}
            variant="outlined"
            sx={{
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button
            onClick={handleConfirmEdit}
            variant="contained"
            disabled={!editWorkspaceName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Workspace</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{' '}
          {workspaceToDeleteName != null
            ? `the workspace "${workspaceToDeleteName}"`
            : 'this workspace'}
          ? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            <CloseIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CanvasWorkspaceTabs;
